"use client";

type ShortcutCallback = (event: KeyboardEvent) => void;

export interface ShortcutRegistration {
	id: string;
	key: string;
	callback: ShortcutCallback;
	disabled: boolean;
	layer: number; // Higher number = higher priority layer
}

export class KeyboardShortcutsManager {
	// Using a Map where each key maps to an array (stack) of registrations.
	private shortcuts: Map<string, ShortcutRegistration[]> = new Map();
	private initialized = false;

	constructor() {
		// Initialize only when running in the browser.
		if (typeof window !== "undefined") {
			this.initialize();
		}
	}

	private initialize() {
		if (this.initialized) return;
		document.addEventListener("keydown", (e) => this.handleKeyDown(e));
		this.initialized = true;
	}

	private handleKeyDown(event: KeyboardEvent) {
		const pressedKey = event.key.toLowerCase();

		// For non-Escape keys, skip if focus is on an input element
		if (pressedKey !== "escape") {
			const activeElement = document.activeElement;
			if (
				activeElement instanceof HTMLInputElement ||
				activeElement instanceof HTMLTextAreaElement ||
				activeElement?.getAttribute("contenteditable") === "true"
			) {
				return;
			}
		}

		// Ignore events with modifier keys (adjust if needed).
		if (event.metaKey || event.altKey || event.ctrlKey) {
			return;
		}

		const registrations = this.shortcuts.get(pressedKey);
		if (!registrations || registrations.length === 0) return;

		// Filter for active (non-disabled) registrations.
		const activeRegs = registrations.filter((reg) => !reg.disabled);
		if (activeRegs.length === 0) return;

		// Determine the highest layer among active registrations.
		const highestLayer = Math.max(...activeRegs.map((reg) => reg.layer));

		// From those in the highest layer, select the most recently registered one.
		const highestLayerRegs = activeRegs.filter(
			(reg) => reg.layer === highestLayer,
		);
		const selected = highestLayerRegs[highestLayerRegs.length - 1];

		if (selected) {
			// Stop event propagation and prevent default BEFORE calling the callback
			event.preventDefault();
			event.stopPropagation();
			event.stopImmediatePropagation();

			// Call the callback
			selected.callback(event);
		}
	}

	/**
	 * Registers a keyboard shortcut.
	 *
	 * @param key The key (case-insensitive) to listen for.
	 * @param callback The callback to invoke when the key is pressed.
	 * @param disabled If true, the shortcut is disabled.
	 * @param layer The layer (priority) of the shortcut. Higher layers take precedence.
	 * @returns A unique ID for the registration.
	 */
	registerShortcut(
		key: string,
		callback: ShortcutCallback,
		disabled: boolean = false,
		layer: number = 0,
	): string {
		// Ensure the manager is initialized (in case this is called post-hydration).
		if (!this.initialized && typeof window !== "undefined") {
			this.initialize();
		}

		const id = Math.random().toString(36).substring(2);
		const registration: ShortcutRegistration = {
			id,
			key,
			callback,
			disabled,
			layer,
		};
		const lowerKey = key.toLowerCase();

		const registrations = this.shortcuts.get(lowerKey) || [];
		registrations.push(registration);
		this.shortcuts.set(lowerKey, registrations);

		return id;
	}

	/**
	 * Unregisters a shortcut given its unique ID.
	 *
	 * @param id The registration ID to remove.
	 */
	unregisterShortcut(id: string) {
		for (const [key, registrations] of this.shortcuts.entries()) {
			const idx = registrations.findIndex((reg) => reg.id === id);
			if (idx !== -1) {
				registrations.splice(idx, 1);
				if (registrations.length === 0) {
					this.shortcuts.delete(key);
				}
				return;
			}
		}
	}

	/**
	 * Updates an existing shortcut registration.
	 *
	 * @param id The registration ID.
	 * @param updates An object containing the properties to update.
	 */
	updateShortcut(
		id: string,
		updates: Partial<Omit<ShortcutRegistration, "id">>,
	) {
		for (const [key, registrations] of this.shortcuts.entries()) {
			const idx = registrations.findIndex((reg) => reg.id === id);
			if (idx !== -1) {
				const current = registrations[idx];
				const updated = { ...current, ...updates };
				registrations[idx] = updated;

				// If the key has changed, remove from the current key and register under the new key.
				if (updates.key && updates.key.toLowerCase() !== key) {
					registrations.splice(idx, 1);
					if (registrations.length === 0) {
						this.shortcuts.delete(key);
					}
					this.registerShortcut(
						updated.key,
						updated.callback,
						updated.disabled,
						updated.layer,
					);
				}
				return;
			}
		}
	}
}

// Create and export a single global instance of the manager.
export const globalKeyboardShortcutsManager = new KeyboardShortcutsManager();
